// Genera la imagen ya recortada a partir del área que devuelve react-easy-crop
// (en píxeles de la imagen original, no del lienzo en pantalla) — usado por
// RecorteFotoModal.jsx en los dos flujos de carga de foto (archivo y cámara).
// Dibuja esa porción sobre un canvas del tamaño exacto del recorte y lo
// exporta como Blob JPEG: el backend no se entera de nada de este paso, sigue
// recibiendo un JPEG normal por el mismo endpoint de siempre.

// Ajustes de color del editor de foto, en porcentaje (100 = sin cambios).
// "intensidad" es la saturación del color: 0 = blanco y negro, 100 = original.
export const AJUSTES_FOTO_SIN_CAMBIOS = { brillo: 100, contraste: 100, intensidad: 100 };

export const hayAjustesDeFoto = (ajustes) =>
  !!ajustes &&
  (ajustes.brillo !== 100 || ajustes.contraste !== 100 || ajustes.intensidad !== 100);

// Mismo orden y misma fórmula que las funciones brightness() / contrast() /
// saturate() de CSS, para que lo que se ve en la vista previa del editor
// (filter de CSS sobre la imagen) sea lo que termina en el JPEG.
export const filtroCssDeAjustes = (ajustes) =>
  `brightness(${ajustes.brillo / 100}) contrast(${ajustes.contraste / 100}) saturate(${ajustes.intensidad / 100})`;

const acotar = (v) => (v < 0 ? 0 : v > 255 ? 255 : v);

// Aplica los ajustes directo sobre los píxeles (RGBA) en vez de usar
// ctx.filter: ese atributo del canvas no existe en todos los navegadores
// (Safari), y esto funciona igual en cualquiera. Modifica `data` en el lugar.
export function aplicarAjustesAPixeles(data, ajustes) {
  const b = ajustes.brillo / 100;
  const c = ajustes.contraste / 100;
  const s = ajustes.intensidad / 100;
  for (let i = 0; i < data.length; i += 4) {
    let r = acotar(data[i] * b);
    let g = acotar(data[i + 1] * b);
    let bl = acotar(data[i + 2] * b);

    r = acotar((r - 127.5) * c + 127.5);
    g = acotar((g - 127.5) * c + 127.5);
    bl = acotar((bl - 127.5) * c + 127.5);

    const nr = (0.213 + 0.787 * s) * r + (0.715 - 0.715 * s) * g + (0.072 - 0.072 * s) * bl;
    const ng = (0.213 - 0.213 * s) * r + (0.715 + 0.285 * s) * g + (0.072 - 0.072 * s) * bl;
    const nb = (0.213 - 0.213 * s) * r + (0.715 - 0.715 * s) * g + (0.072 + 0.928 * s) * bl;

    data[i] = acotar(Math.round(nr));
    data[i + 1] = acotar(Math.round(ng));
    data[i + 2] = acotar(Math.round(nb));
  }
}

const cargarImagen = (src) =>
  new Promise((resolve, reject) => {
    const imagen = new Image();
    imagen.addEventListener('load', () => resolve(imagen));
    imagen.addEventListener('error', reject);
    // Necesario para poder leer el canvas después con toBlob() cuando la
    // imagen viene de una URL cross-origin (acá siempre es local — dataURL u
    // object URL del propio navegador — pero no cuesta nada dejarlo puesto).
    imagen.crossOrigin = 'anonymous';
    imagen.src = src;
  });

export async function generarImagenRecortada(imagenSrc, areaRecortePx, calidad = 0.9, ajustes = AJUSTES_FOTO_SIN_CAMBIOS) {
  const imagen = await cargarImagen(imagenSrc);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(areaRecortePx.width);
  canvas.height = Math.round(areaRecortePx.height);

  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    imagen,
    areaRecortePx.x,
    areaRecortePx.y,
    areaRecortePx.width,
    areaRecortePx.height,
    0,
    0,
    canvas.width,
    canvas.height
  );

  if (hayAjustesDeFoto(ajustes)) {
    const pixeles = ctx.getImageData(0, 0, canvas.width, canvas.height);
    aplicarAjustesAPixeles(pixeles.data, ajustes);
    ctx.putImageData(pixeles, 0, 0);
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('No se pudo generar la imagen recortada'));
    }, 'image/jpeg', calidad);
  });
}
